import React, { useEffect, useMemo, useState } from 'react'
import Modal from '../Modal'
import ProjectService from '../../services/project.service'
import SearchSmallIcon from '../../assets/icon/SearchSmallIcon'
import { Project } from '../../types'
import styles from './index.module.scss'

interface TaskMoveModalProps {
    isOpen: boolean
    currentProjectId: number | string
    title?: string
    onClose: () => void
    onConfirm: (project: Project) => Promise<void> | void
}

function extractProjects(data: any): Project[] {
    if (Array.isArray(data)) {
        return data
    }

    if (Array.isArray(data?.projects)) {
        return data.projects
    }

    if (Array.isArray(data?.items)) {
        return data.items
    }

    return []
}

export default function TaskMoveModal({
    isOpen,
    currentProjectId,
    title = 'Переместить задачу',
    onClose,
    onConfirm,
}: TaskMoveModalProps) {
    const [projects, setProjects] = useState<Project[]>([])
    const [search, setSearch] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const filteredProjects = useMemo(() => (
        projects.filter((project) => (
            String(project.id) !== String(currentProjectId)
            && !project.is_archived
            && !project.archived_at
            && (project.name || '').toLowerCase().includes(search.toLowerCase())
        ))
    ), [currentProjectId, projects, search])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        let isMounted = true
        setSearch('')

        const loadProjects = async () => {
            setIsLoading(true)

            try {
                const projectList = await ProjectService.list()

                if (isMounted) {
                    setProjects(extractProjects(projectList))
                }
            } catch (error) {
                if (isMounted) {
                    setProjects([])
                }

                console.error('Failed to load projects for task move:', error)
            } finally {
                if (isMounted) {
                    setIsLoading(false)
                }
            }
        }

        void loadProjects()

        return () => {
            isMounted = false
        }
    }, [isOpen])

    const handleClose = () => {
        if (!isSubmitting) {
            onClose()
        }
    }

    const handleConfirm = async (project: Project) => {
        setIsSubmitting(true)

        try {
            await onConfirm(project)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={handleClose} width={420}>
            <div className={styles.taskMoveModal}>
                <button type="button" className={styles.taskMoveModalClose} onClick={handleClose} disabled={isSubmitting}>
                    <img src="/icon/closeCircle.svg" alt="close" />
                </button>

                <div className={styles.taskMoveModalTitle}>{title}</div>

                <div className={styles.taskMoveModalSearch}>
                    <SearchSmallIcon />
                    <input
                        className={styles.taskMoveModalSearchInput}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Найти проект"
                        autoFocus
                    />
                </div>

                <div className={styles.taskMoveModalList}>
                    {isLoading ? (
                        <div className={styles.taskMoveModalEmpty}>Загружаем проекты...</div>
                    ) : filteredProjects.length ? (
                        filteredProjects.map((project) => (
                            <button
                                type="button"
                                key={project.id}
                                className={styles.taskMoveModalItem}
                                onClick={() => handleConfirm(project)}
                                disabled={isSubmitting}
                            >
                                <span className={styles.taskMoveModalItemColor} style={{ borderColor: project.color || '#453CFB' }} />
                                <span className={styles.taskMoveModalItemName}>{project.name}</span>
                            </button>
                        ))
                    ) : (
                        <div className={styles.taskMoveModalEmpty}>Нет доступных проектов</div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
